import type { CallCost, UsageEntry } from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@hostesa/ui';
import { namespace } from './i18n';

/** Parse a decimal-string money/quantity value; falls back to 0 on garbage. */
function toNumber(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

export interface UsageSummaryProps {
  entries: UsageEntry[];
  /** Optional rolled-up cost for the selected call. */
  cost?: CallCost | null;
  timezone: string;
}

/**
 * Tenant/call-level usage ledger. Each row is a single usage entry; the footer
 * sums totalCost. When there are no entries we show a calm EmptyState rather
 * than an empty grid (master prompt §13 — loading/empty/error always handled).
 */
export function UsageSummary({ entries, cost, timezone }: UsageSummaryProps) {
  const { t } = useT(namespace);
  const f = useFormatters(timezone);

  if (entries.length === 0) {
    return (
      <Card data-testid="usage-summary-empty">
        <CardHeader>
          <CardTitle>{t('summary.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            data-testid="usage-summary-empty-state"
            title={t('summary.emptyTitle')}
            description={t('summary.emptyDescription')}
          />
        </CardContent>
      </Card>
    );
  }

  const currency = cost?.currency ?? entries[0]?.currency ?? 'EUR';
  const total = cost
    ? toNumber(cost.totalCost)
    : entries.reduce((sum, e) => sum + toNumber(e.totalCost), 0);

  return (
    <Card data-testid="usage-summary">
      <CardHeader>
        <CardTitle>{t('summary.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table data-testid="usage-summary-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('summary.column.type')}</TableHead>
              <TableHead>{t('summary.column.provider')}</TableHead>
              <TableHead>{t('summary.column.quantity')}</TableHead>
              <TableHead>{t('summary.column.unitCost')}</TableHead>
              <TableHead>{t('summary.column.totalCost')}</TableHead>
              <TableHead>{t('summary.column.when')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id} data-testid={`usage-row-${entry.id}`}>
                <TableCell>{entry.usageType}</TableCell>
                <TableCell>{typeof entry.provider === 'string' ? entry.provider : '—'}</TableCell>
                <TableCell>{f.formatNumber(toNumber(entry.quantity))}</TableCell>
                <TableCell>
                  {f.formatCurrency(toNumber(entry.unitCost), { currency: entry.currency })}
                </TableCell>
                <TableCell>
                  {f.formatCurrency(toNumber(entry.totalCost), { currency: entry.currency })}
                </TableCell>
                <TableCell>{f.formatDateTime(entry.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div
          className="flex items-center justify-between border-t border-[var(--color-border)] pt-3 mt-1"
          data-testid="usage-summary-total"
        >
          <span className="text-sm text-[var(--color-stone-mid)]">{t('summary.totalLabel')}</span>
          <span className="font-[var(--font-mono)] text-sm font-medium text-[var(--color-charcoal)]">
            {f.formatCurrency(total, { currency })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
